import React from 'react';
import styled from 'styled-components';
import { Background, Border } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import Vendor from 'data/Vendor';
import { BusinessUnit } from 'data/BusinessUnit';
import User, { hasOfmpAdminProfile } from 'data/User';
import { isOfmpAdmin } from 'data/User';
import AdminViewToggle from './AdminViewToggle';
import BrandLogoDisplay from './BrandLogoDisplay';
import VendorSelectionDropdown from './VendorSelectionDropdown';
import BusinessUnitSelectionDropdown from './BusinessUnitSelectionDropdown';
import UserProfileMenu from './UserProfileMenu';
import { useVendorBusinessUnitSelection } from 'hooks/useVendorBusinessUnitSelection';
import { useUserModal } from 'hooks/useUserModal';
import { useUserRole } from 'hooks/useUserRole';
import { useBusinessUnit } from 'context/businessUnit';


const HeaderContainer = styled.div`
  width: calc(100% - 220px);
  height: 80px;
  background-color: ${Background.shaded};
  display: flex;
  align-items: center;
  padding: 0 ${Space.sp3x};
  box-shadow: 0 4px 8px rgba(74, 78, 81, 0.2);
  gap: ${Space.sp3x};
  border-bottom: 1px solid ${Border.main};
  position: fixed;
  top: 0;
  left: 220px; /* Align with the main content area (sidebar width) */
  z-index: 1000;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Space.sp3x};
  flex: 1;
  justify-content: flex-start;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Space.sp3x};
`;


interface VendorBrandSelectionHeaderProps {
  user?: User | null;
  vendorsList: Vendor[];
  selectedVendor: Vendor | null;
  updateVendor: (vendorName: string, brandId?: number) => void;
  selectedBusinessUnit: BusinessUnit | null;
  updateBusinessUnit: (businessUnitName: string | null) => void;
  disabled?: boolean;
  isAdminView?: boolean;
  onAdminViewToggle?: (isAdminView: boolean) => void;
  disableBusinessUnitSelector?: boolean;
}

/**
 * Main header component for vendor and business unit selection
 *
 * Features:
 * - Brand logo display
 * - Vendor/Business unit selection dropdowns (hidden in admin view)
 * - Admin view toggle (for OFMP admins only)
 * - User profile menu with logout/settings
 * - Responsive design with proper spacing
 */
const VendorBrandSelectionHeader: React.FC<VendorBrandSelectionHeaderProps> = ({
  user,
  vendorsList,
  selectedVendor,
  updateVendor,
  selectedBusinessUnit,
  updateBusinessUnit,
  disabled = false,
  isAdminView = false,
  onAdminViewToggle,
  disableBusinessUnitSelector = false
}) => {
  // Get business unit list from context
  const { businessUnitList } = useBusinessUnit();

  // Custom hooks for functionality separation
  const { displayUserRole } = useUserRole({ user: user || null, selectedVendor, selectedBusinessUnit });

  const {
    vendorOptions,
    businessUnitOptions,
    brandLogoUrl
  } = useVendorBusinessUnitSelection({
    vendorsList,
    businessUnitList,
    selectedVendor,
    selectedBusinessUnit
  });

  const {
    isUserModalOpen,
    toggleUserModal,
    handleLogout,
    handleAccountClick,
    handleSettingsClick
  } = useUserModal();

  return (
    <HeaderContainer>
      <BrandLogoDisplay
        logoUrl={brandLogoUrl}
        brandName={selectedBusinessUnit?.brand?.name}
        isAdminView={isAdminView}
      />

      <LeftSection>
        <VendorSelectionDropdown
          selectedVendorName={selectedVendor?.name || ''}
          vendorOptions={vendorOptions}
          onVendorChange={(vendorName) => updateVendor(vendorName)}
          disabled={disabled}
          isAdminView={isAdminView}
        />

        <BusinessUnitSelectionDropdown
          selectedBusinessUnitName={selectedBusinessUnit?.name || ''}
          businessUnitOptions={businessUnitOptions}
          onBusinessUnitChange={updateBusinessUnit}
          disabled={disabled || disableBusinessUnitSelector}
          isAdminView={isAdminView}
        />
      </LeftSection>

      <RightSection>
        {user && (hasOfmpAdminProfile(user) || isOfmpAdmin(user)) && onAdminViewToggle && (
          <AdminViewToggle
            isAdminView={isAdminView}
            onToggle={onAdminViewToggle}
            disabled={disabled}
          />
        )}

        <UserProfileMenu
          userName={user?.name || ''}
          displayUserRole={displayUserRole}
          isUserModalOpen={isUserModalOpen}
          onToggleModal={toggleUserModal}
          onAccountClick={handleAccountClick}
          onSettingsClick={handleSettingsClick}
          onLogout={handleLogout}
        />
      </RightSection>
    </HeaderContainer>
  );
};

VendorBrandSelectionHeader.displayName = 'VendorBrandSelectionHeader';

export default VendorBrandSelectionHeader;